import React from 'react';
import { Duration } from 'luxon';

import makeStyles from '@mui/styles/makeStyles';

import { Grid, Table, TableBody, TableRow, TableCell } from '@mui/material';

import { sortedDropdownItemLabels } from '../../utils/utils';

const useStyles = makeStyles((theme) => ({
  root: {},
  emptyRow: {
    height: '30px',
  },
  titleRow: {
    fontSize: '1.2rem',
    fontWeight: 600,
    background: '#ff8c6e',
  },
  usersRow: {
    fontSize: '1.1rem',
    fontWeight: 500,
    background: '#ffccbb',
  },
  tableColumnLabel: {
    textAlign: 'center',
    fontWeight: 600,
    background: '#d6d6d6',
  },
  tableRowLabel: {
    fontWeight: 600,
    background: '#e6e6e6',
  },
  normalTableCell: {
    textAlign: 'center',
  },
  boldTableCell: {
    fontWeight: 600,
    textAlign: 'center',
  },
}));

const filterForTemperature = (statsForSelectedUsers, temp) => {
  return statsForSelectedUsers.map((statsForSingleUser) => filterUserStatsForTemperature(statsForSingleUser, temp));
};

const filterUserStatsForTemperature = (statsForSingleUser, temp) => {
  const groupsFilteredForTemp = filterActivityGroupsForTemperature(statsForSingleUser.activityGroups, temp);
  return { ...statsForSingleUser, activityGroups: groupsFilteredForTemp };
};

const filterActivityGroupsForTemperature = (activityGroups, temp) => {
  return activityGroups.map((group) => filterActivityGroupForTemperature(group, temp));
};

const filterActivityGroupForTemperature = (group, temp) => {
  const filteredActivities = filterActivitiesForTemperature(group.activities, temp);
  return { ...group, activities: filteredActivities };
};

const filterActivitiesForTemperature = (activities, temp) => {
  return activities.filter((activity) => activity.otherPartyTemp === temp);
};

// statsForThisTemp is full elaborate structure
// output should be a simple array of { date, ... } objects
const collectActivitiesOfType = (statsForThisTemp, activityType) => {
  let activitiesOfType = [];
  statsForThisTemp.forEach((statsForUser) => {
    const activityGroupsForUser = statsForUser.activityGroups;
    const groupForType = activityGroupsForUser.find((group) => group.type === activityType);
    if (groupForType) {
      activitiesOfType = [...activitiesOfType, ...groupForType.activities];
    }
  });
  return activitiesOfType;
};

// activities should be a simple array of { date, ... } objects
const filterForTimeRange = (activities, startDate, limitDate) => {
  return activities.filter(
    (activity) => activity.date.valueOf() >= startDate.valueOf() && activity.date.valueOf() < limitDate.valueOf()
  );
};

const ReportPreview = ({
  startDate,
  lastIncludedDate,
  allUsers,
  selectedUsers,
  allStats,
  activityTypes,
  matrixValuesSetter,
}) => {
  const classes = useStyles();

  const temperatures = sortedDropdownItemLabels('Prospect Status').filter((item) => item !== 'Archived');

  const nColumns = activityTypes.length + 2;
  const limitDate = lastIncludedDate.plus(Duration.fromObject({ days: 1 }));

  const selectedPersonIds = selectedUsers.map((user) => user.personId);
  const statsForSelectedUsers = allStats.filter((userStats) => selectedPersonIds.indexOf(userStats.personId) >= 0);

  const emptyRow = <TableRow className={classes.emptyRow} />;
  const startDateString = startDate.toFormat('D'); // 'localized numeric date'
  const lastIncludedDateString = lastIncludedDate.toFormat('D');
  const dateRange = `${startDateString} - ${lastIncludedDateString}`;

  const titleRow = (
    <TableRow>
      <TableCell className={classes.titleRow} colSpan={nColumns}>
        Activity Report for {dateRange}
      </TableCell>
    </TableRow>
  );

  const userNames = selectedUsers.map((user) => user.name).join(', ');
  const usersRow = (
    <TableRow>
      <TableCell className={classes.usersRow} colSpan={nColumns}>
        Users: {userNames}
      </TableCell>
    </TableRow>
  );

  const headerRow = (
    <TableRow>
      <TableCell className={classes.tableColumnLabel}>Contact Temp</TableCell>
      {activityTypes.map((aType) => (
        <TableCell key={aType} className={classes.tableColumnLabel}>
          Activity: {aType}
        </TableCell>
      ))}
      <TableCell className={classes.tableColumnLabel}>Total</TableCell>
    </TableRow>
  );

  const matrixValues = {};

  let rowTotal = 0;
  const createRowForTemperature = (temp) => {
    matrixValues[temp] = [];
    const statsForThisTemp = filterForTemperature(statsForSelectedUsers, temp);
    rowTotal = 0;
    return (
      <TableRow key={temp}>
        <TableCell className={classes.tableRowLabel}>{temp}</TableCell>
        {activityTypes.map((activityType, j) => {
          const statsForThisActivityType = collectActivitiesOfType(statsForThisTemp, activityType);
          const statsInTimeRange = filterForTimeRange(statsForThisActivityType, startDate, limitDate);
          const nActivities = statsInTimeRange.length;
          rowTotal += nActivities;
          matrixValues[temp].push(nActivities);
          return (
            <TableCell className={classes.normalTableCell} key={j}>
              {nActivities}
            </TableCell>
          );
        })}
        <TableCell className={classes.boldTableCell}>{rowTotal}</TableCell>
      </TableRow>
    );
  };

  matrixValuesSetter(matrixValues);

  return (
    <Grid container spacing={2}>
      <Grid item md={12}>
        <Table>
          <TableBody>
            {titleRow}
            {usersRow}
            {emptyRow}
            {headerRow}
            {temperatures.map((temp) => createRowForTemperature(temp))}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};

export default ReportPreview;
